/**
 * Justified-rows photo gallery (Flickr / Google Photos style).
 *
 * Photos keep their natural aspect ratio and fill each row edge-to-edge. Ratios
 * are measured from each thumbnail as it loads (cached), so the layout reflows
 * from a sensible default into the true layout with no extra network.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { IconCheck } from '@tabler/icons-react';
import { Media } from '../media/Media.tsx';
import { computeJustifiedLayout, type LayoutInput } from '../../lib/justifiedLayout.ts';
import { useAspectRatios, getCachedRatio } from '../../hooks/useAspectRatios.ts';
import { resolveSignedUrl } from '../../hooks/useSignedUrl.ts';
import { formatSize } from '../../lib/media.ts';
import { cn } from '../../lib/cn.ts';
import type { MediaItem } from '../../lib/types.ts';

interface JustifiedGalleryProps {
  items: MediaItem[];
  selection: Set<string>;
  selectionActive: boolean;
  targetRowHeight?: number;
  onOpen: (index: number) => void;
  onToggleSelect: (key: string) => void;
}

function useContainerWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setWidth(Math.floor(w));
    });
    ro.observe(el);
    setWidth(Math.floor(el.getBoundingClientRect().width));
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

export function JustifiedGallery({
  items,
  selection,
  selectionActive,
  targetRowHeight = 240,
  onOpen,
  onToggleSelect,
}: JustifiedGalleryProps) {
  const { ref, width } = useContainerWidth();
  const { getRatio, report, version } = useAspectRatios();

  // Eagerly measure each thumbnail's intrinsic size via a detached Image, so
  // the justified layout is correct from first paint instead of waiting on the
  // lazy/in-viewport <img> to decode. Uses the CDN URL (no extra mint) and
  // skips anything already measured. A small worker pool bounds concurrency.
  useEffect(() => {
    let cancelled = false;
    const pending = items.filter((it) => getCachedRatio(it.thumbnailKey) === undefined);
    let cursor = 0;
    const WORKERS = 12;

    const measureNext = (): void => {
      if (cancelled || cursor >= pending.length) return;
      const item = pending[cursor++];
      resolveSignedUrl(item.thumbnailKey)
        .then((url) => {
          if (cancelled) return;
          const img = new Image();
          img.onload = () => {
            if (!cancelled) report(item.thumbnailKey, img.naturalWidth, img.naturalHeight);
            measureNext();
          };
          img.onerror = () => measureNext();
          img.src = url;
        })
        .catch(() => measureNext());
    };

    for (let i = 0; i < WORKERS; i++) measureNext();
    return () => {
      cancelled = true;
    };
  }, [items, report]);

  const layoutInputs: LayoutInput[] = useMemo(
    // Ratios are keyed by thumbnailKey (what's actually measured).
    () => items.map((it) => ({ key: it.key, ratio: getRatio(it.thumbnailKey) ?? 0 })),
    // `version` bumps each time a thumbnail reports its measured ratio, so the
    // layout reflows from the default ratio to the real ones as images decode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, getRatio, version],
  );

  const { tiles, totalHeight } = useMemo(
    () => computeJustifiedLayout(layoutInputs, width, targetRowHeight, 8),
    [layoutInputs, width, targetRowHeight],
  );

  const indexByKey = useMemo(() => {
    const m = new Map<string, number>();
    items.forEach((it, i) => m.set(it.key, i));
    return m;
  }, [items]);

  return (
    <div ref={ref} className="relative w-full" style={{ height: totalHeight || undefined }}>
      {tiles.map((tile) => {
        const index = indexByKey.get(tile.key) ?? 0;
        const item = items[index];
        if (!item) return null;
        const selected = selection.has(item.key);
        return (
          <div
            key={tile.key}
            className="absolute"
            style={{ width: tile.width, height: tile.height, top: tile.top, left: tile.left }}
          >
            <div
              className={cn(
                'group relative h-full w-full overflow-hidden rounded-lg',
                'ring-0 transition duration-200 ease-standard hover:z-10 hover:shadow-lg',
                selected && 'ring-2 ring-accent ring-offset-2 ring-offset-transparent',
              )}
            >
              <button
                onClick={() => onToggleSelect(item.key)}
                aria-label={selected ? `Deselect ${item.displayName}` : `Select ${item.displayName}`}
                aria-pressed={selected}
                // Wide 44px hit target on touch, small visual checkbox inside.
                className="absolute left-0 top-0 z-20 grid h-11 w-11 place-items-center"
              >
                <span
                  className={cn(
                    'ml-2 mt-2 grid h-6 w-6 place-items-center rounded-md border transition duration-200 ease-standard',
                    selected
                      ? 'border-accent bg-accent text-white'
                      : 'border-white/70 bg-black/30 text-transparent opacity-0 backdrop-blur group-hover:opacity-100',
                    selectionActive && 'opacity-100',
                  )}
                >
                  <IconCheck className="h-4 w-4" />
                </span>
              </button>

              <button
                onClick={() => onOpen(index)}
                className="block h-full w-full text-left"
                aria-label={`Open ${item.displayName}`}
              >
                <Media
                  fileKey={item.thumbnailKey}
                  kind={item.kind}
                  alt={item.displayName}
                  priority={index < 12}
                  videoBadge={item.kind === 'video'}
                  className="h-full w-full transition-transform duration-200 ease-standard group-hover:scale-[1.02]"
                  onNaturalSize={(w, h) => report(item.thumbnailKey, w, h)}
                />
                {/* hover gradient + filename */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs font-medium text-white">{item.displayName}</span>
                    {item.size != null && (
                      <span className="shrink-0 text-[10px] text-white/70">{formatSize(item.size)}</span>
                    )}
                  </div>
                </div>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
